import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import mapboxgl from "mapbox-gl";
import { useParamsContext } from "../../contexts";
import { ColourCircleC } from "../ui/ColourCircle";
import { CardC } from "../ui/Cards";

const Popup = ({
  map,
  activeFeature,
  setActiveFeature,
  userLocations,
  activeCustomer,
  setActiveCustomer,
  userRole,
}) => {
  const { getTexture, getColour } = useParamsContext();

  // a ref to hold the popup instance
  const popupRef = useRef();
  // a ref for an element to hold the popup's content
  const contentRef = useRef(document.createElement("div"));

  // instantiate the popup on mount (per map instance), remove it on unmount
  useEffect(() => {
    if (!map) return;

    // create a new popup instance, but do not set its location or content yet
    popupRef.current = new mapboxgl.Popup({
      closeOnClick: false,
      offset: 20,
      focusAfterOpen: false,
      maxWidth: "400px",
    });

    popupRef.current.on("close", () => {
      setActiveFeature(null); // when the popup is closed, set activeFeature to null
      setActiveCustomer(null);
    });

    return () => {
      if (popupRef.current) {
        popupRef.current.remove();
      }
    };
  }, [map, setActiveFeature, setActiveCustomer]);

  // when activeFeature or activeCustomer changes, update the popup's content
  useEffect(() => {
    if (!activeFeature) {
      popupRef.current.remove(); // remove the popup if no active feature
      return;
    }

    popupRef.current
      .setLngLat(activeFeature.geometry.coordinates) // set its position using activeFeature's geometry
      .setDOMContent(contentRef.current) // use contentRef's DOM node so React updates are reflected
      .addTo(map); // add the popup to the map
  }, [activeFeature, activeCustomer, userLocations, map]);

  // Compute site info based on current activeFeature and activeCustomer
  const site = (() => {
    if (!activeFeature) return null;

    if (userRole === "admin") {
      const location = activeCustomer?.locations.find(
        (loc) => loc.locationId === activeFeature?.properties.locationId
      );
      return location ? `${activeCustomer?.customer} - ${location.name}` : null;
    }
    const location = userLocations?.find(
      (loc) => loc.locationId === activeFeature?.properties.locationId
    );
    return location?.name || null;
  })();

  // use a react portal to render the content to show in the popup, assigning it to contentRef
  return (
    <>
      {createPortal(
        <div>
          <div>
            <strong>Substances detected:</strong>
            <div className='h5'>
              {activeFeature?.properties.findingsDisplay?.length > 0
                ? activeFeature.properties.findingsDisplay.join(", ")
                : "No findings"}
            </div>
          </div>

          <div className='d-flex flex-column justify-content-start align-items-start flex-wrap gap-2'>
            <div>
              <strong>Collected on:</strong>
              <div className='h6 mb-0'>
                {new Date(
                  activeFeature?.properties.processedDate
                ).toLocaleString()}
              </div>
            </div>
            <div className='d-flex flex-row justify-content-start align-items-center gap-3'>
              <div className=''>
                <strong>Sample ID:</strong>{" "}
                <div>
                  {activeFeature?.properties.sampleId || "Sample Details"}
                </div>
              </div>
              <div>
                <strong>Site:</strong> <div>{site}</div>
              </div>

              <div>
                <strong>Texture:</strong>

                <CardC data={getTexture(activeFeature?.properties.texture)} />
              </div>
              <div>
                <strong>Colour:</strong>

                <ColourCircleC
                  data={getColour(activeFeature?.properties.colour)}
                />
              </div>
            </div>
          </div>
          <a
            href={`/sample/${activeFeature?.properties._id}`}
            className='btn btn-outline-primary btn-sm mb-1 mt-3 mx-auto d-block'
          >
            View Sample
          </a>
        </div>,
        contentRef.current
      )}
    </>
  );
};

export default Popup;
